import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// headerRowIndexes (optional): 0-based line indexes of the header rows within
// dataString, from getHeaderLineIndexes() in core/dataParser.js. When omitted,
// header rows are detected by the "date/time" sniff below, as before.
const DownloadExcel = async (dataString, siteinfo, headerRowIndexes) => {
  // Process the input string to convert it to a 2D array
  const rows = dataString.split("\n");
  const data = rows.map((row) => row.split("\t").map(parseCellValue));

  // Create a new workbook and worksheet
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("SAS Logs");
  const doWrapText = true;

  // Define styles for the header area
  const headerAreaStyle = {
    fill: {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "F1F2F4" }, // Light yellow background for header area
    },
    font: {
      bold: true,
      color: { argb: "000000" }, // Black font color
    },
    alignment: {
      horizontal: "center",
      vertical: "middle",
      wrapText: doWrapText, // Enable text wrapping
    },
  };

  // Define header text and split into lines
  const headerText =
    "Surf 1-3 Web Application By S.& A.S.Ltd.Co\n" +
    "Exported Date: " +
    new Date() +
    "\n" +
    "Date Constraint: from " +
    siteinfo.from +
    " to " +
    siteinfo.to +
    "\n" +
    "Surf ID: " +
    siteinfo.siteId +
    "\n" +
    "Data Type: " +
    siteinfo.dataType +
    "\n" +
    "https://sascontrollers.com/\n" +
    "Designed And Developed By S.& A.S.Ltd.Co";
  const headerLines = headerText.split("\n");

  // Define the number of rows to merge based on the number of header lines
  const numberOfHeaderRows = headerLines.length;

  // Add header area information
  worksheet.mergeCells(`A1:G${numberOfHeaderRows}`); // Adjust the range dynamically
  const headerAreaCell = worksheet.getCell("A1");
  headerAreaCell.value = headerText; // Add header text
  headerAreaCell.style = headerAreaStyle;

  // Define alternating row colors
  let rowColors = []; // Light yellow and light blue
  if (siteinfo.dataType.toLowerCase() == "timelog") {
    rowColors = ["FFF2CC", "DDEBF7"];
  } else if (siteinfo.dataType.toLowerCase() == "eventlog") {
    rowColors = ["C9DAF8", "FFE599"];
  } else if (siteinfo.dataType.toLowerCase() == "chartlog") {
    rowColors = ["D9EAD3", "C9DAF8"];
  } else if (siteinfo.dataType.toLowerCase() == "faultlog") {
    rowColors = ["FFF2CC", "FCE4D6"];
  }
  // Add data to the worksheet starting from the row after the header
  data.forEach((row, rowIndex) => {
    const excelRow = worksheet.addRow(row.map((cell) => cell.value));

    // Apply alternating colors
    const colorIndex = rowIndex % rowColors.length;
    const rowColor = rowColors[colorIndex];

    excelRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      // Apply background color and center-align the content, including cells with value 0
      //console.log("colNumbrt:" + colNumber + " cell:" + cell);
      if (
        cell.value !== null &&
        cell.value !== undefined &&
        cell.value !== ""
      ) {
        cell.style = {
          fill: {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: rowColor },
          },
          alignment: {
            horizontal: "center",
            vertical: "middle",
            wrapText: doWrapText,
          },
        };
      }
    });
  });

  // Define styles for the headers
  const headerStyle = {
    fill: {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "522D3B" }, // background
    },
    font: {
      bold: true,
      color: { argb: "FFFFFF" }, // font color
    },
    alignment: {
      horizontal: "center",
      vertical: "center",
    },
  };

  // Data line i (0-based into dataString.split("\n")) lands on worksheet row
  // i + numberOfHeaderRows + 1, because the merged A1:G7 banner materialises
  // rows 1..7 and addRow() appends after them.
  const explicitHeaderRows =
    headerRowIndexes && headerRowIndexes.length
      ? new Set(headerRowIndexes.map((i) => i + numberOfHeaderRows + 1))
      : null;

  // Apply styles to the headers
  worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    if (
      rowNumber === numberOfHeaderRows + 1 || // Adjust based on header rows
      (explicitHeaderRows
        ? explicitHeaderRows.has(rowNumber)
        : row.values[1] &&
          row.values[1] != undefined &&
          row.values[1].toString().toLowerCase().includes("date/time"))
    ) {
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.style = headerStyle;
      });
    }
  });

  // Auto-fit columns based on header length
  worksheet.columns.forEach((column, index) => {
    let maxLength = 0;
    const maxWidth = 25; // Set a maximum width to prevent overly wide columns

    // Get the length of the header in the first row (or adjust for your header row)
    const header = worksheet
      .getRow(numberOfHeaderRows + 1)
      .getCell(index + 1).value;
    const headerLength = header ? header.toString().length : 0;

    // Consider the header length as the minimum required length
    maxLength = headerLength;

    // Calculate the length of the longest cell content
    column.eachCell({ includeEmpty: true }, (cell) => {
      const cellLength = cell.value ? cell.value.toString().length : 0;
      if (cellLength > maxLength) {
        maxLength = cellLength;
      }
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: doWrapText,
      };
    });

    // Set the column width with a maximum constraint
    column.width = Math.min(maxLength + 2, maxWidth);
  });

  // Write to buffer
  const buffer = await workbook.xlsx.writeBuffer();

  // Create a Blob from the buffer
  const blob = new Blob([buffer], { type: "application/octet-stream" });

  // Use file-saver to save the Blob as an Excel file
  //saveAs(blob, `${siteinfo.dataType} for ${siteinfo.siteId}.xlsx`);
  saveAs(
    blob,
    `${siteinfo.siteId}_${siteinfo.dataType}_${siteinfo.from}_${siteinfo.to}.xlsx`
  );
};

export default DownloadExcel;

// Helper function to detect and convert cell types
const parseCellValue = (value) => {
  // Trim for safety
  const trimmedValue = value.trim();

  // Detect number
  if (!isNaN(trimmedValue) && trimmedValue !== "") {
    return { value: parseFloat(trimmedValue), type: "number" };
  }

  // Detect proper date patterns (YYYY-MM-DD, DD/MM/YYYY, MM/DD/YYYY, etc.)
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}(T.*)?$/; // ISO-like
  const slashDateRegex = /^\d{1,2}\/\d{1,2}\/\d{4}$/; // Common US or EU style
  const dashDateRegex = /^\d{1,2}-\d{1,2}-\d{4}$/; // Common alternate

  if (
    isoDateRegex.test(trimmedValue) ||
    slashDateRegex.test(trimmedValue) ||
    dashDateRegex.test(trimmedValue)
  ) {
    const date = new Date(trimmedValue);
    if (!isNaN(date.getTime())) {
      return { value: date.toLocaleString(), type: "datetime" };
    }
  }

  // Default: treat as plain string
  return { value: value.toString(), type: "string" };
};

/*const parseCellValue = (value) => {
  // Check if the value is a number
  if (!isNaN(parseFloat(value)) && isFinite(value)) {
    return { value: parseFloat(value), type: "number" };
  }

  // Check if the value is a date (simple heuristic)
  const date = new Date(value);
  if (!isNaN(date.getTime())) {
    // Format the date to include both date and time
    const formattedDateTime = date.toLocaleString(); // You can customize the format here
    return { value: formattedDateTime, type: "datetime" };
  }

  // Default to text
  return { value: value.toString(), type: "string" };
};*/
